/**
 * usePodTemplateWebSocket - Real-time pod template updates via dedicated WebSocket
 *
 * Connects to the /ws/pod-templates endpoint for real-time instance and CTA events.
 * This enables live updates in the pod template view (instance list, status changes, CTA counts).
 *
 * @example
 * ```tsx
 * const { isConnected } = usePodTemplateWebSocket({
 *   templateId: '69caa5a920a47ad59762fe6f',
 *   onInstanceCreated: (instance) => {
 *     setInstances(prev => [instance, ...prev]);
 *   },
 *   onInstanceUpdated: (instance) => {
 *     setInstances(prev => prev.map(i => i.id === instance.id ? { ...i, ...instance } : i));
 *   },
 *   onCtaCreated: () => {
 *     setCtaCount(prev => prev + 1);
 *   },
 * });
 * ```
 *
 * @module hooks/usePodTemplateWebSocket
 */

import { useEffect, useRef, useCallback, useState } from "react";
import {
  buildWebSocketUrl,
  canAttemptWebSocketAuth,
  shouldReconnectAfterWebSocketClose,
} from "@/utils/wsAuth";
import { useAuth } from "@/context/AuthContext";

// ============================================================================
// Type Definitions
// ============================================================================

/**
 * Pod instance state within WebSocket events.
 */
export interface PodInstanceState {
  current_status: string;
  current_stage: string;
}

/**
 * Pod instance payload received in WebSocket events.
 */
export interface PodInstancePayload {
  id: string;
  name: string;
  state: PodInstanceState;
  created_at?: string;
  updated_at?: string;
}

/**
 * CTA payload received when a new CTA is created.
 */
export interface CtaPayload {
  id: string;
  pod_instance_id: string;
  message: string;
  priority: "high" | "medium" | "low";
  status: string;
  category: string;
}

/**
 * Event received when a new pod instance is created.
 */
export interface InstanceCreatedEvent {
  type: "instance_created";
  template_id: string;
  company_id: string;
  instance: PodInstancePayload;
}

/**
 * Event received when a pod instance is updated.
 */
export interface InstanceUpdatedEvent {
  type: "instance_updated";
  template_id: string;
  company_id: string;
  instance: PodInstancePayload;
}

/**
 * Event received when a pod instance is deleted.
 */
export interface InstanceDeletedEvent {
  type: "instance_deleted";
  template_id: string;
  company_id: string;
  instance_id: string;
}

/**
 * Event received when a new CTA is created.
 */
export interface CtaCreatedEvent {
  type: "cta_created";
  template_id: string;
  company_id: string;
  cta: CtaPayload;
}

/**
 * Event received when a CTA is completed.
 */
export interface CtaCompletedEvent {
  type: "cta_completed";
  template_id: string;
  company_id: string;
  cta_id: string;
}

/**
 * Event received when a CTA is cancelled.
 */
export interface CtaCancelledEvent {
  type: "cta_cancelled";
  template_id: string;
  company_id: string;
  cta_id: string;
}

/**
 * Ping event for keepalive.
 */
interface PingEvent {
  type: "ping";
}

/**
 * Subscription confirmation event.
 */
interface SubscribedEvent {
  type: "subscribed";
  template_id: string;
}

/**
 * Unsubscription confirmation event.
 */
interface UnsubscribedEvent {
  type: "unsubscribed";
  template_id: string;
}

/**
 * Union of all possible Pod Template WebSocket events.
 */
export type PodTemplateEvent =
  | InstanceCreatedEvent
  | InstanceUpdatedEvent
  | InstanceDeletedEvent
  | CtaCreatedEvent
  | CtaCompletedEvent
  | CtaCancelledEvent
  | PingEvent
  | SubscribedEvent
  | UnsubscribedEvent;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get the WebSocket URL for Pod Templates, converting to wss:// if on HTTPS.
 */
function getPodTemplateWebSocketUrl(): string {
  const baseUrl = import.meta.env.VITE_WEBSOCKET_URL || "ws://localhost:8001";
  const normalizedBase = baseUrl.replace(/\/$/, "");

  const wsUrl = normalizedBase.endsWith("/ws")
    ? `${normalizedBase}/pod-templates`
    : `${normalizedBase}/ws/pod-templates`;

  // Convert ws:// to wss:// if on HTTPS
  if (
    typeof window !== "undefined" &&
    window.location.protocol === "https:" &&
    wsUrl.startsWith("ws://")
  ) {
    return wsUrl.replace("ws://", "wss://");
  }

  return wsUrl;
}

// ============================================================================
// Hook Interface
// ============================================================================

/**
 * Options for the usePodTemplateWebSocket hook.
 */
export interface UsePodTemplateWebSocketOptions {
  /** Template ID to subscribe to */
  templateId?: string;
  /** Callback when a new instance is created */
  onInstanceCreated?: (instance: PodInstancePayload) => void;
  /** Callback when an instance is updated */
  onInstanceUpdated?: (instance: PodInstancePayload) => void;
  /** Callback when an instance is deleted */
  onInstanceDeleted?: (instanceId: string) => void;
  /** Callback when a new CTA is created */
  onCtaCreated?: (cta: CtaPayload) => void;
  /** Callback when a CTA is completed */
  onCtaCompleted?: (ctaId: string) => void;
  /** Callback when a CTA is cancelled */
  onCtaCancelled?: (ctaId: string) => void;
  /** Whether the subscription is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Return value from usePodTemplateWebSocket hook.
 */
export interface UsePodTemplateWebSocketReturn {
  /** Whether the WebSocket is connected */
  isConnected: boolean;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for subscribing to real-time pod template updates via dedicated WebSocket.
 *
 * @param options - Configuration options for the subscription
 * @returns Object containing connection status
 */
export function usePodTemplateWebSocket({
  templateId,
  onInstanceCreated,
  onInstanceUpdated,
  onInstanceDeleted,
  onCtaCreated,
  onCtaCompleted,
  onCtaCancelled,
  enabled = true,
}: UsePodTemplateWebSocketOptions): UsePodTemplateWebSocketReturn {
  const { isAuthenticated, webSocketAuthReady, isLoading: authLoading } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);

  // Store callbacks in refs to avoid reconnection on callback changes
  const onInstanceCreatedRef = useRef(onInstanceCreated);
  const onInstanceUpdatedRef = useRef(onInstanceUpdated);
  const onInstanceDeletedRef = useRef(onInstanceDeleted);
  const onCtaCreatedRef = useRef(onCtaCreated);
  const onCtaCompletedRef = useRef(onCtaCompleted);
  const onCtaCancelledRef = useRef(onCtaCancelled);

  onInstanceCreatedRef.current = onInstanceCreated;
  onInstanceUpdatedRef.current = onInstanceUpdated;
  onInstanceDeletedRef.current = onInstanceDeleted;
  onCtaCreatedRef.current = onCtaCreated;
  onCtaCompletedRef.current = onCtaCompleted;
  onCtaCancelledRef.current = onCtaCancelled;

  // Store template ID in ref
  const templateIdRef = useRef(templateId);
  templateIdRef.current = templateId;

  const connect = useCallback(() => {
    if (!mountedRef.current) return;

    // Don't connect if already connected or connecting
    if (wsRef.current && wsRef.current.readyState <= 1) {
      return;
    }

    // Don't connect if no template ID
    if (!templateIdRef.current) {
      return;
    }

    if (!canAttemptWebSocketAuth(isAuthenticated, webSocketAuthReady)) {
      return;
    }

    try {
      let hadOpened = false;
      const wsUrl = buildWebSocketUrl(getPodTemplateWebSocketUrl());
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        hadOpened = true;
        if (!mountedRef.current) {
          ws.close();
          return;
        }

        setIsConnected(true);

        // Subscribe to template updates
        if (templateIdRef.current) {
          ws.send(
            JSON.stringify({
              action: "subscribe",
              template_id: templateIdRef.current,
            })
          );
        }
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;

        try {
          const data: PodTemplateEvent = JSON.parse(event.data);

          switch (data.type) {
            case "instance_created":
              onInstanceCreatedRef.current?.(data.instance);
              break;

            case "instance_updated":
              onInstanceUpdatedRef.current?.(data.instance);
              break;

            case "instance_deleted":
              onInstanceDeletedRef.current?.(data.instance_id);
              break;

            case "cta_created":
              onCtaCreatedRef.current?.(data.cta);
              break;

            case "cta_completed":
              onCtaCompletedRef.current?.(data.cta_id);
              break;

            case "cta_cancelled":
              onCtaCancelledRef.current?.(data.cta_id);
              break;

            case "ping":
              ws.send(JSON.stringify({ action: "pong" }));
              break;

            case "subscribed":
              console.log(
                `[usePodTemplateWebSocket] Subscribed to template ${data.template_id}`
              );
              break;

            case "unsubscribed":
              console.log(
                `[usePodTemplateWebSocket] Unsubscribed from template ${data.template_id}`
              );
              break;
          }
        } catch (err) {
          console.error("[usePodTemplateWebSocket] Failed to parse message:", err);
        }
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;

        setIsConnected(false);
        wsRef.current = null;

        if (
          !shouldReconnectAfterWebSocketClose(event?.code ?? 0, event?.reason ?? "", {
            hadOpened,
          })
        ) {
          return;
        }

        reconnectTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            connect();
          }
        }, 3000);
      };

      ws.onerror = (error) => {
        console.error("[usePodTemplateWebSocket] WebSocket error:", error);
        ws.close();
      };
    } catch (err) {
      console.error("[usePodTemplateWebSocket] Failed to create WebSocket:", err);
    }
  }, [isAuthenticated, webSocketAuthReady]);

  // Update ref when templateId changes (without triggering reconnect)
  useEffect(() => {
    templateIdRef.current = templateId;
  }, [templateId]);

  // Single connection effect
  const hasTemplateId = !!templateId;

  useEffect(() => {
    mountedRef.current = true;

    if (authLoading || !enabled || !hasTemplateId) {
      return;
    }

    if (!canAttemptWebSocketAuth(isAuthenticated, webSocketAuthReady)) {
      return;
    }

    // Defer connection slightly to mitigate React Strict Mode double-mount issues
    connectTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) {
        connect();
      }
    }, 50);

    return () => {
      mountedRef.current = false;

      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
        connectTimeoutRef.current = null;
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      if (wsRef.current) {
        const ws = wsRef.current;
        ws.onopen = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        ws.close();
        wsRef.current = null;
      }

      setIsConnected(false);
    };
  }, [enabled, hasTemplateId, connect, authLoading, isAuthenticated, webSocketAuthReady]);

  return {
    isConnected,
  };
}

export default usePodTemplateWebSocket;
